/*
 * Copyright (c) 2021. World Wide Generation Ltd
 */

import { AlignmentType, ILevelsOptions, LevelFormat } from 'docx';

export const declarativeStyles = [
  {
    id: 'Normal',
    name: 'Normal',
    quickFormat: true,
    run: {
      size: 20,
      font: 'Arial',
    },
  },
  {
    id: 'Heading1',
    name: 'Heading 1',
    basedOn: 'Normal',
    next: 'Normal',
    quickFormat: true,
    run: {
      size: 36,
      bold: false,
      font: 'Arial'
    },
    paragraph: {
      spacing: {
        before: 300,
        after: 200,
      },
    },
  },
  {
    id: 'Heading2',
    name: 'Heading 2',
    basedOn: 'Normal',
    next: 'Normal',
    quickFormat: true,
    run: {
      size: 30,
      font: 'Arial'
    },
    paragraph: {
      spacing: {
        before: 240,
        after: 120,
      },
    },
  },
  {
    id: 'Heading3',
    name: 'Heading 3',
    basedOn: 'Normal',
    next: 'Normal',
    quickFormat: true,
    run: {
      size: 26,
      font: 'Arial',
      bold: true
    },
    paragraph: {
      spacing: {
        before: 240,
        after: 120,
      },
    },
  },
  {
    id: 'plain',
    name: 'Plain',
    basedOn: 'Normal',
    next: 'Normal',
    quickFormat: true,
    run: {
      size: 20,
      font: 'Arial'
    }
  },
  {
    id: 'plainWhite',
    name: 'Plain White',
    basedOn: 'Normal',
    next: 'Normal',
    quickFormat: true,
    run: {
      size: 20,
      font: 'Arial',
      color: 'FFFFFF'
    },
  },
  {
    id: 'bold',
    name: 'Bold',
    basedOn: 'Normal',
    next: 'Normal',
    quickFormat: true,
    run: {
      size: 20,
      font: 'Arial',
      bold: true
    },
  },
]

export const blueStyles = [
  {
    id: 'Normal',
    name: 'Normal',
    quickFormat: true,
    run: {
      size: 24,
      font: 'Century Gothic',
    },
  },
  {
    id: 'Title',
    name: 'Title',
    basedOn: 'Normal',
    next: 'Normal',
    quickFormat: true,
    run: {
      color: '5189BD',
      size: 148,
      bold: false,
    },
    paragraph: {
      spacing: {
        before: 300,
        after: 200,
      },
    },
  },
  {
    id: 'Heading1',
    name: 'Heading 1',
    basedOn: 'Normal',
    next: 'Normal',
    quickFormat: true,
    run: {
      color: '5189BD',
      size: 80,
      bold: false,
    },
    paragraph: {
      spacing: {
        before: 300,
        after: 200,
      },
    },
  },
  {
    id: 'Heading2',
    name: 'Heading 2',
    basedOn: 'Normal',
    next: 'Normal',
    quickFormat: true,
    run: {
      color: '1A4791',
      size: 50,
    },
    paragraph: {
      spacing: {
        before: 240,
        after: 120,
      },
    },
  },
  {
    id: 'Heading3',
    name: 'Heading 3',
    basedOn: 'Normal',
    next: 'Normal',
    quickFormat: true,
    run: {
      color: '657DA6',
      size: 26,
      bold: true
    },
    paragraph: {
      spacing: {
        before: 240,
        after: 120,
      },
    },
  },
  {
    id: 'plain',
    name: 'Plain',
    basedOn: 'Normal',
    next: 'Normal',
    quickFormat: true,
    run: {
      size: 20,
    }
  },
  {
    id: 'plainWhite',
    name: 'Plain White',
    basedOn: 'Normal',
    next: 'Normal',
    quickFormat: true,
    run: {
      size: 20,
      font: 'Arial',
      color: 'FFFFFF'
    },
  },
  {
    id: 'bold',
    name: 'Bold',
    basedOn: 'Normal',
    next: 'Normal',
    quickFormat: true,
    run: {
      size: 20,
      bold: true
    },
  },
];

export enum BulletFormatLevel {
  DECIMAL = 0,
  LOWER_LETTER = 1
}

const levels: ILevelsOptions[] = [
  {
    level: BulletFormatLevel.DECIMAL,
    format: LevelFormat.DECIMAL,
    text: '%1.',
    alignment: AlignmentType.START,
    style: {
      paragraph: {
        indent: { left: 720, hanging: 260 }
      }
    }
  },
  {
    level: BulletFormatLevel.LOWER_LETTER,
    format: LevelFormat.LOWER_LETTER,
    text: '%2)',
    alignment: AlignmentType.START,
    style: {
      paragraph: {
        indent: { left: 720, hanging: 260 }
      }
    }
  }
];

export const numberingStyles: {
  reference: string,
  levels: ILevelsOptions[]
}[] = [
    {
      reference: 'numbering-decimal',
      levels: levels
    },
    {
      reference: 'numbering-letter-one',
      levels: levels
    },
    {
      reference: 'numbering-letter-two',
      levels: levels
    },
    {
      reference: 'numbering-letter-three',
      levels: levels
    },
    {
      reference: 'numbering-letter-four',
      levels: levels
    },
    {
      reference: 'numbering-list-one',
      levels: levels
    },
    {
      reference: 'numbering-list-two',
      levels: levels
    },
    {
      reference: 'numbering-list-three',
      levels: levels
    },
    {
      reference: 'numbering-list-four',
      levels: levels
    },
    {
      reference: 'numbering-list-one',
      levels: levels
    },
    {
      reference: 'numbering-list-two',
      levels: levels
    },
    {
      reference: 'numbering-list-three',
      levels: levels
    },
    {
      reference: 'numbering-list-four',
      levels: levels
    },
    {
      reference: 'numbering-list-one',
      levels: levels
    },
    {
      reference: 'numbering-list-two',
      levels: levels
    },
    {
      reference: 'numbering-list-three',
      levels: levels
    },
    {
      reference: 'numbering-list-four',
      levels: levels
    },
    {
      reference: 'numbering-list-five',
      levels: levels
    },
    {
      reference: 'numbering-list-six',
      levels: levels
    },
    {
      reference: 'numbering-list-seven',
      levels: levels
    },
    {
      reference: 'numbering-list-eight',
      levels: levels
    },
    {
      reference: 'numbering-list-nine',
      levels: levels
    },
    {
      reference: 'numbering-list-ten',
      levels: levels
    },
    {
      reference: 'numbering-list-eleven',
      levels: levels
    },
    {
      reference: 'numbering-list-twelve',
      levels: levels
    }
  ]
